/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Orders Screen
 * 
 * Displays paginated list of user orders
 * Features:
 * - Responsive grid (1-4 columns based on screen width)
 * - Pull-to-refresh functionality
 * - Load more (pagination) on scroll end
 * - Order cards with print option
 * - Navigation to order details
 * 
 * @component
 * @param {Object} navigation - React Navigation object
 */
import React, { useEffect, useState } from 'react';
import { FlatList, RefreshControl, InteractionManager, StyleSheet, useWindowDimensions, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';
import { enableRefreshControl, fetchOrders, handleLoadMore, resetData } from '../store/orders.slice';
import { ActivityIndicator, Text } from 'react-native-paper';
import { theme } from '../theme/theme';
import OrderCard from '../components/OrderCard';
import HttpLoader from '../components/HttpLoader';

/**
 * Calculates number of columns for responsive order grid
 * @param {number} width - Screen width in pixels
 * @returns {number} Number of columns (1-4)
 */
const getNumColumns = (width) => {
    if (width < 500) { return 1; }
    if (width < 576) { return 1; }
    if (width < 768) { return 2; }
    if (width < 992) { return 3; }
    if (width < 1200) { return 3; }
    if (width < 1400) { return 4; }
    return 4;
};

function Orders({ navigation }) {
    const dispatch = useDispatch();
    const { width } = useWindowDimensions();
    const numColumns = getNumColumns(width);
    const [interactionCompleted, setIntersectionCompleted] = useState(false);
    const orders = useSelector((state) => state.orders?.orders);
    const status = useSelector((state) => state.orders?.status);
    const error = useSelector((state) => state.orders?.error);
    const page = useSelector((state) => state.orders?.page);
    const refreshing = useSelector((state) => state.orders?.refreshing);
    const loadingMore = useSelector((state) => state.orders?.loadingMore);


    useEffect(() => {
        const task = InteractionManager.runAfterInteractions(async () => {
            setIntersectionCompleted(true);
        });
        return () => {
            dispatch(resetData());
            task.cancel();
        };
    }, []);

    useEffect(() => {
        if (interactionCompleted) {
            let params = {
                page,
            };
            if (status !== 'loading') {
                dispatch(fetchOrders({ ...params, numColumns }));
            }
        }
    }, [page, interactionCompleted]);

    const renderItem = ({ item }) => {
        if (item.isEmpty) {
            return <View key={item?.order_id} style={{ flex: 1 }} />;
        }
        return (
            <View key={item?.order_id} style={{ flex: 1, margin: 1 }}>
                <OrderCard order={item} />
            </View>
        );
    };

    const _refreshControl = () => {
        return (
            <RefreshControl
                colors={[theme.colors.primary]}
                refreshing={refreshing}
                onRefresh={() => _onRefresh()}
            />
        );
    };

    const _onRefresh = (retry) => {
        let params = {
            page,
        };
        dispatch(fetchOrders({ ...params, numColumns }));
        dispatch(enableRefreshControl({ retry: retry }));
    };

    const _handleLoadMore = () => {
        if (loadingMore && status !== 'loading' && orders.length >= 0) {
            dispatch(handleLoadMore());
        }
    };

    const ListFooterComponent = () => {
        if (!loadingMore) {
            return null;
        }
        return (
            <View style={{ padding: 16 }}>
                <ActivityIndicator animating={true} color={theme.colors.primary} />
            </View>
        );
    };

    return (
        <SafeAreaView edges={['bottom']} style={styles.container}>
            {interactionCompleted &&
                <React.Fragment>
                    <HttpLoader status={status} errorHandler={_onRefresh} error={error} />
                    {status === 'succeeded' &&
                        <View style={styles.mainContainer}>
                            {orders?.length === 0 ? (
                                <Text variant="titleMedium" style={{ textAlign: 'center' }}>No orders found</Text>
                            ) : (<FlatList
                                data={orders}
                                renderItem={renderItem}
                                keyExtractor={(item) => (item?.order_id).toString()}
                                onEndReached={_handleLoadMore}
                                onEndReachedThreshold={0.1}
                                refreshControl={_refreshControl()}
                                contentContainerStyle={{ paddingBottom: 64 }}
                                ListFooterComponent={ListFooterComponent}
                                key={numColumns}
                                numColumns={numColumns}
                                columnWrapperStyle={numColumns > 1 ? { gap: 1 } : undefined} // RN 0.71+
                            />)
                            }
                        </View>
                    }
                </React.Fragment>
            }
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#f1f1f3',
    },
    mainContainer: {
        flex: 1,
        margin: 8,
        // paddingBottom: 64,
        // marginHorizontal: 8,
    },
    orderStatus: {
        fontSize: 10,
        textTransform: 'capitalize',
        color: '#ffffff',
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
    },
    btnStyle: {
        // flex: 1,
        paddingVertical: 0,
        marginVertical: 0,
    },
    labelStyle: {
        fontSize: 12,
        paddingVertical: 0,
        textTransform: 'capitalize',
    },
});

export default Orders;
